<!--@format-->
<template>
    <div class="create-stripe-account-widget" :class="componentClasses">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : 'Create Stripe Account' }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else class="widget-container">
                    <div class="payment edit">
                        <div class="form">
                            <app-input
                                v-model="companyName"
                                label="Company Name"
                                placeholder=" "
                                :is-valid="getIsValidField('companyName')"
                                :class="{ 'has-errors': !getIsValidField('companyName') }"
                                error="Required"
                            />
                        </div>
                    </div>
                    <div class="payment edit">
                        <div class="form">
                            <label>Select a country</label>
                            <app-dropdown
                                v-model="countryCode" 
                                :items="countryOptions" 
                                :is-valid="getIsValidField('countryCode')"
                                :class="{ 'has-errors': !getIsValidField('countryCode') }"
                                error="Required"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" :disabled="processing" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <app-button class="save-btn" :loading="processing" @click="saveData">
                    Next
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppInput = () => import('@/components/common/app-input');
const AppDropdown = () => import('@/components/common/app-dropdown');
import { responsiveHelpers } from '@/scripts/helpers';
import { PaymentMethodsService } from '@/scripts/services/payment-methods/payment-methods-service';
import EventBus from '@/scripts/event-bus.js';
import { ResponsiveMixin } from 'vue-responsive-components';
import { required } from 'vuelidate/lib/validators';
import { LogLevel } from '@/scripts/logger';

export default {
    name: 'create-stripe-account-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-input': AppInput,
        'app-dropdown': AppDropdown,
    },

    mixins: [ResponsiveMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            processing: false,
            companyName: null,
            countryCode: null,
            countryOptions: [
                {
                    name: 'United States',
                    value: 'US',
                },
                {
                    name: 'Australia',
                    value: 'AU',
                },
                {
                    name: 'Canada',
                    value: 'CA',
                },
                {
                    name: 'Mexico',
                    value: 'MX',
                },
                {
                    name: 'Ireland',
                    value: 'IE',
                },
                {
                    name: 'Netherlands',
                    value: 'NL',
                },
                {
                    name: 'New Zealand',
                    value: 'NZ',
                },
                {
                    name: 'United Kingdom',
                    value: 'GB',
                },
            ],
            accountId: this.widget.accountId,
        };
    },

    computed: {
        hasAgencyBillingPaymentPermission() {
            return (
                this.$store.getters.isBillingPaymentUser ||
                this.$store.getters.isOrganizationUser ||
                this.$store.getters.isMasterAgencyUser ||
                this.$store.getters.isAgencyAdmin
            );
        },

        isAgencyContext() {
            return this.$store.state.context.agency.id;
        },

        componentClasses() {
            return [responsiveHelpers.getBreakpointClasses(this)];
        },
    },

    breakpoints: responsiveHelpers.getBreakpoints(),

    validations() {
        return {
            companyName: {
                required,
            },
            countryCode: {
                required,
            }
        };
    },

    created() {
        this.validateRoles();
    },

    methods: {
        validateRoles() {
            if (this.isAgencyContext && !this.hasAgencyBillingPaymentPermission) {
                this.closeWidget();
                const errorMessage =
                    'You need additional permissions in order to perform this action.';
                this.$helpers.showFailNotification(errorMessage, { level: LogLevel.INFO });
            }
        },

        getIsValidField(field) {
            const isInvalidValid = this.$v[field].$invalid && this.$v[field].$dirty;
            return !isInvalidValid;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        saveData() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            const request = {
                ConnectedAccountName: this.companyName,
                CountryCode: this.countryCode.value,
            };
            this.processing = true;
            PaymentMethodsService.createConnectedAccount(request, this.accountId)
                .then(() => {
                    this.$helpers.showSuccessNotification('Billing saved successfully.');
                    EventBus.$emit('payment-method:update', true);
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your billing.',
                        { error },
                    );
                })
                .finally(() => {
                    this.processing = false;
                });
        },
    },
};
</script>

<style>
.create-stripe-account-widget {
    color: #382153;
}

.create-stripe-account-widget .save-btn {
    width: 128px;
    height: 40px;
}

.create-stripe-account-widget .content-wrapper .payment.edit {
    margin-bottom: 12px;
}

.create-stripe-account-widget .cancel-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    font: normal normal medium 14px/24px Roboto;
    color: #877d88;
    min-width: 128px;
    min-height: 40px;
}

.create-stripe-account-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.create-stripe-account-widget label {
    display: block;
    font: 500 16px/1em Roboto;
    margin-bottom: 7px;
    color: #382153;
}

.create-stripe-account-widget .app-input.error.has-errors input,
.create-stripe-account-widget .gray-container.has-errors {
    border: 2px solid #f30070;
}

.create-stripe-account-widget .app-input.error label {
    color: #382153;
}
</style>
